import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { deleteByIndex, setIn } from 'utils/data';

import {
  selectAutomatedActionCriteriaSchema,
  selectLoanActionCriteriaSchema,
} from 'store/system/selectors';

import ExpressionField from '../../form/ExpressionField';
import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';

const LoanActionClosingPortalAccessRow = ({
  index,
  rootPath,
  isDisabled,
  isEditAllowed,
  isSingleItem,
  isAutomatedActions = false,
}) => {
  const {
    form,
    setInForm,
    setValidationErrors,
    validationErrors,
    handleChange,
    validateField,
    staticValidateField,
  } = useFormContext();
  const automatedSchemaParams = useSelector(selectAutomatedActionCriteriaSchema);
  const loanActionSchemaParams = useSelector(selectLoanActionCriteriaSchema);

  const schemaParams = isAutomatedActions ? automatedSchemaParams : loanActionSchemaParams;

  const fieldPath = `${rootPath}[${index}]`;
  const emailExpressionPath = `${fieldPath}.emailFriendlyExpression`;

  const forms = _.get(form, rootPath);

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];

    newForms.splice(index, 1);
    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  return (
    <div className="flex">
      <ExpressionField
        required
        className="mt-8 mb-8 grow"
        dataTestId={loanManagementTestIds.CONTENT_VIEW_EMAIL_EXPRESSION_INPUT}
        disabled={isDisabled || !isEditAllowed}
        error={!!_.get(validationErrors, emailExpressionPath)}
        handleChange={handleChange}
        helperText={_.get(validationErrors, emailExpressionPath)}
        id="closingPortalEmail"
        isOnlyBooleanAllowed={false}
        label="Email Expression"
        name={emailExpressionPath}
        schemaParams={schemaParams}
        setInForm={setInForm}
        staticValidateField={staticValidateField}
        validateField={validateField}
        value={_.get(form, emailExpressionPath) || ''}
      />
      {isEditAllowed && !isSingleItem && (
        <ButtonWithTooltip
          data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
          disabled={isDisabled}
          icon={DeleteIcon}
          size="medium"
          spanContainerClassName="flex items-center"
          title="Delete row"
          onClick={onDeleteRow}
        />
      )}
    </div>
  );
};

LoanActionClosingPortalAccessRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionClosingPortalAccessRow);
